/* eslint-disable */
import { bitmaskPrefix } from "./typedKeys";
import { SocketMessageInterface } from "../interfaces/Monitoring.interface";

const DOOR_SUFFIXES = [
  "Usage:FrontDoorOpened",
  "Usage:FrontDoorClosed",
  "Usage:FrontDoorOpening",
  "Usage:FrontDoorClosing",
  "Usage:RearDoorOpened",
  "Usage:RearDoorClosed",
  "Usage:RearDoorOpening",
  "Usage:RearDoorClosing",
];

function hasDoorData(bitmask: Record<string, boolean>, prefix: string): boolean {
  return DOOR_SUFFIXES.some((s) => `${prefix}${s}` in bitmask);
}

/**
 * Resolve an elevator icon name directly from bitmask entries.
 * Direction (Up/Down) takes priority; when idle, falls through to door state.
 * Returns null when no door data has been received (no warning for absent data).
 */
export function bitmaskToIcon(bitmask: Record<string, boolean>, prefix: string): string | null {
  const up = bitmask[`${prefix}Usage:Up`] ?? false;
  const down = bitmask[`${prefix}Usage:Down`] ?? false;

  if (up && !down) return "elUp";
  if (down && !up) return "elDown";

  const frontOpen = bitmask[`${prefix}Usage:FrontDoorOpened`] ?? false;
  const frontClosed = bitmask[`${prefix}Usage:FrontDoorClosed`] ?? false;
  const frontOpening = bitmask[`${prefix}Usage:FrontDoorOpening`] ?? false;
  const frontClosing = bitmask[`${prefix}Usage:FrontDoorClosing`] ?? false;
  const rearOpen = bitmask[`${prefix}Usage:RearDoorOpened`] ?? false;
  const rearClosed = bitmask[`${prefix}Usage:RearDoorClosed`] ?? false;
  const rearOpening = bitmask[`${prefix}Usage:RearDoorOpening`] ?? false;
  const rearClosing = bitmask[`${prefix}Usage:RearDoorClosing`] ?? false;

  const frontTransition = frontOpening || frontClosing;
  const rearTransition = rearOpening || rearClosing;

  // Transition states (opening/closing) take priority over steady states
  if (frontTransition && rearTransition) return "elFrontOpeningRearOpening";
  if (frontTransition && rearOpen) return "elFrontOpeningRearOpen";
  if (frontTransition && rearClosed) return "elFrontOpeningRearClose";
  if (frontOpen && rearTransition) return "elFrontOpenRearOpening";
  if (frontClosed && rearTransition) return "elFrontCloseRearOpening";
  if (frontTransition) return "elOpening";
  if (rearTransition) return "elRearOpening";

  if (frontOpen && rearOpen) return "elFrontOpenRearOpen";
  if (frontOpen && rearClosed) return "elFrontOpenRearClose";
  if (frontClosed && rearOpen) return "elFrontCloseRearOpen";
  if (frontClosed && rearClosed) return "elFrontCloseRearClose";
  if (frontOpen) return "elOpen";
  if (frontClosed) return "elClose";
  if (rearOpen) return "elRearOpen";
  if (rearClosed) return "elRearClose";

  // No door data received — don't show warning for absent data
  if (!hasDoorData(bitmask, prefix)) return null;
  return "elWarning";
}

/** Convenience: resolve icon from a SocketMessageInterface. */
export function socketMessageToIcon(msg: SocketMessageInterface): string | null {
  const p = bitmaskPrefix(msg.bankId, msg.liftId);
  return bitmaskToIcon(msg.bitmask, p);
}

/**
 * Resolve door icon only from bitmask entries (ignores direction).
 * Returns null when no door data has been received.
 */
export function bitmaskToDoorIcon(
  bitmask: Record<string, boolean>,
  prefix: string,
  doorUnknownAsOpening?: boolean,
): string | null {
  const frontOpen = bitmask[`${prefix}Usage:FrontDoorOpened`] ?? false;
  const frontClosed = bitmask[`${prefix}Usage:FrontDoorClosed`] ?? false;
  const frontOpening = bitmask[`${prefix}Usage:FrontDoorOpening`] ?? false;
  const frontClosing = bitmask[`${prefix}Usage:FrontDoorClosing`] ?? false;
  const rearOpen = bitmask[`${prefix}Usage:RearDoorOpened`] ?? false;
  const rearClosed = bitmask[`${prefix}Usage:RearDoorClosed`] ?? false;
  const rearOpening = bitmask[`${prefix}Usage:RearDoorOpening`] ?? false;
  const rearClosing = bitmask[`${prefix}Usage:RearDoorClosing`] ?? false;

  const frontTransition = frontOpening || frontClosing;
  const rearTransition = rearOpening || rearClosing;

  // Transition states (opening/closing) take priority over steady states
  if (frontTransition && rearTransition) return "elFrontOpeningRearOpening";
  if (frontTransition && rearOpen) return "elFrontOpeningRearOpen";
  if (frontTransition && rearClosed) return "elFrontOpeningRearClose";
  if (frontOpen && rearTransition) return "elFrontOpenRearOpening";
  if (frontClosed && rearTransition) return "elFrontCloseRearOpening";
  if (frontTransition) return "elOpening";
  if (rearTransition) return "elRearOpening";

  if (frontOpen && rearOpen) return "elFrontOpenRearOpen";
  if (frontOpen && rearClosed) return "elFrontOpenRearClose";
  if (frontClosed && rearOpen) return "elFrontCloseRearOpen";
  if (frontClosed && rearClosed) return "elFrontCloseRearClose";
  if (frontOpen) return "elOpen";
  if (frontClosed) return "elClose";
  if (rearOpen) return "elRearOpen";
  if (rearClosed) return "elRearClose";

  // No door data received — don't show warning/opening for absent data
  if (!hasDoorData(bitmask, prefix)) return null;
  return doorUnknownAsOpening ? "elClose" : "elWarning";
}

/**
 * Resolve door icon from bitmask entries, filtering out unconfigured doors.
 * When hideUnconfigured is true, door signals for doors that don't exist
 * on this lift/floor are ignored (treated as not present).
 *
 * @param bankHasMixedDoors - If true, bank has both front and rear doors across floors,
 *                            so show double icons (e.g., elFrontCloseRearOpen).
 *                            If false, bank has only one door type, so show simple icons (e.g., elOpen).
 */
export function bitmaskToDoorIconFiltered(
  bitmask: Record<string, boolean>,
  prefix: string,
  hasFrontDoor: boolean,
  hasRearDoor: boolean,
  doorUnknownAsOpening?: boolean,
  bankHasMixedDoors?: boolean,
): string | null {
  const frontOpen = hasFrontDoor && (bitmask[`${prefix}Usage:FrontDoorOpened`] ?? false);
  const frontClosed = hasFrontDoor && (bitmask[`${prefix}Usage:FrontDoorClosed`] ?? false);
  const frontOpening = hasFrontDoor && (bitmask[`${prefix}Usage:FrontDoorOpening`] ?? false);
  const frontClosing = hasFrontDoor && (bitmask[`${prefix}Usage:FrontDoorClosing`] ?? false);
  const rearOpen = hasRearDoor && (bitmask[`${prefix}Usage:RearDoorOpened`] ?? false);
  const rearClosed = hasRearDoor && (bitmask[`${prefix}Usage:RearDoorClosed`] ?? false);
  const rearOpening = hasRearDoor && (bitmask[`${prefix}Usage:RearDoorOpening`] ?? false);
  const rearClosing = hasRearDoor && (bitmask[`${prefix}Usage:RearDoorClosing`] ?? false);

  const frontTransition = frontOpening || frontClosing;
  const rearTransition = rearOpening || rearClosing;

  // If bank has mixed door configuration, always show double icons
  if (bankHasMixedDoors) {
    // Debug logging

    // Check floor door configuration first, then determine icon based on states

    // When floor has only front door (rear is assumed closed)
    if (hasFrontDoor && !hasRearDoor) {
      const icon = frontTransition
        ? "elFrontOpeningRearClose"
        : frontOpen
        ? "elFrontOpenRearClose"
        : "elFrontCloseRearClose";
      return icon;
    }

    // When floor has only rear door (front is assumed closed)
    if (!hasFrontDoor && hasRearDoor) {
      const icon = rearTransition
        ? "elFrontCloseRearOpening"
        : rearOpen
        ? "elFrontCloseRearOpen"
        : "elFrontCloseRearClose";
      return icon;
    }

    // When floor has both doors configured
    if (hasFrontDoor && hasRearDoor) {
      // Transition states (both doors)
      if (frontTransition && rearTransition) return "elFrontOpeningRearOpening";
      if (frontTransition && rearOpen) return "elFrontOpeningRearOpen";
      if (frontTransition && rearClosed) return "elFrontOpeningRearClose";
      if (frontOpen && rearTransition) return "elFrontOpenRearOpening";
      if (frontClosed && rearTransition) return "elFrontCloseRearOpening";

      // Steady states (both doors)
      if (frontOpen && rearOpen) return "elFrontOpenRearOpen";
      if (frontOpen && rearClosed) return "elFrontOpenRearClose";
      if (frontClosed && rearOpen) return "elFrontCloseRearOpen";
      if (frontClosed && rearClosed) return "elFrontCloseRearClose";
    }
  } else {
    // Bank has only one door type across all floors - use simple icons
    // Transition states
    if (hasFrontDoor && frontTransition) return "elOpening";
    if (hasRearDoor && rearTransition) return "elRearOpening";

    // Steady states
    if (frontOpen) return "elOpen";
    if (frontClosed) return "elClose";
    if (rearOpen) return "elRearOpen";
    if (rearClosed) return "elRearClose";
  }

  // No door data received — don't show warning/opening for absent data
  if (!hasDoorData(bitmask, prefix)) return null;
  return doorUnknownAsOpening ? "elClose" : "elWarning";
}

/** Resolve direction icon only from bitmask entries. Returns null when idle. */
export function bitmaskToDirectionIcon(
  bitmask: Record<string, boolean>,
  prefix: string,
): string | null {
  const up = bitmask[`${prefix}Usage:Up`] ?? false;
  const down = bitmask[`${prefix}Usage:Down`] ?? false;

  if (up && !down) return "elUp";
  if (down && !up) return "elDown";
  return null;
}
